import type { Demand, DemandStatus } from './types'

/**
 * Mirrors the backend enum `MarketplaceDemandStatus`, which has exactly these five
 * values. "Beendet" is not a status — it is the separate `isArchived` flag.
 */
const STATUS_LABELS: Record<DemandStatus, string> = {
  OFFERED: 'Offen',
  INTERESTED_AVAILABLE: 'Offen',
  PARTIALLY_COVERED: 'Teilweise vermittelt',
  COVERED: 'Vermittelt',
  NOT_MATCHABLE: 'Nicht vermittelbar',
}

const STATUS_PALETTES: Record<DemandStatus, string> = {
  OFFERED: 'blue',
  INTERESTED_AVAILABLE: 'blue',
  PARTIALLY_COVERED: 'yellow',
  COVERED: 'green',
  NOT_MATCHABLE: 'red',
}

export function normalizeDemandStatus(status?: string): DemandStatus | undefined {
  if (!status) return undefined

  const upper = status.toUpperCase()

  return upper in STATUS_LABELS ? (upper as DemandStatus) : undefined
}

/**
 * The backend's own rule for whether a SIB may still act on a demand
 * (`MarketplaceDemandService.ensureDemandOpenForUserAction`): archived, fully
 * covered, and not-matchable demands are closed. PARTIALLY_COVERED is still
 * open — there is remaining demand to cover.
 *
 * Unknown statuses count as open so that a new backend value never silently hides
 * the marketplace.
 */
export function isDemandOpen(demand: Pick<Demand, 'status' | 'isArchived'>) {
  if (demand.isArchived) return false

  const status = normalizeDemandStatus(demand.status)

  return status !== 'COVERED' && status !== 'NOT_MATCHABLE'
}

export function getDemandStatusLabel(status?: string) {
  const normalized = normalizeDemandStatus(status)

  return normalized && STATUS_LABELS[normalized]
}

export function getDemandStatusPalette(status?: string) {
  const normalized = normalizeDemandStatus(status)

  return normalized ? STATUS_PALETTES[normalized] : 'gray'
}
